import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  prices: {
    id: string;
    unit_amount: number | null;
    currency: string;
    recurring: { interval: string } | null;
  }[];
}

interface ProductMapping {
  checkout_type: string;
  stripe_product_id: string;
  stripe_price_id: string;
  monthly_rate: number;
  product_name: string;
}

const AdminStripeProducts = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [mappings, setMappings] = useState<Record<string, ProductMapping>>({});
  const [selectedPrices, setSelectedPrices] = useState<Record<string, string>>({
    box_storage: '',
    pickup_storage: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch Stripe products
      const { data: productsData, error: productsError } = await supabase.functions.invoke('list-stripe-products');
      
      if (productsError) throw productsError;
      
      setProducts(productsData.products || []);

      // Fetch existing mappings
      const { data: mappingsData, error: mappingsError } = await supabase
        .from('stripe_product_mappings')
        .select('*');

      if (mappingsError) throw mappingsError;

      const mappingsMap: Record<string, ProductMapping> = {};
      const selectedPricesMap: Record<string, string> = {};
      
      mappingsData?.forEach((mapping) => {
        mappingsMap[mapping.checkout_type] = mapping;
        selectedPricesMap[mapping.checkout_type] = mapping.stripe_price_id;
      });

      setMappings(mappingsMap);
      setSelectedPrices((prev) => ({ ...prev, ...selectedPricesMap }));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load product data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      for (const checkoutType of ['box_storage', 'pickup_storage']) {
        const priceId = selectedPrices[checkoutType];
        if (!priceId) continue;

        // Find the product and price details
        const product = products.find((p) =>
          p.prices.some((price) => price.id === priceId)
        );
        const price = product?.prices.find((p) => p.id === priceId);

        if (!product || !price) continue;

        const mappingData = {
          checkout_type: checkoutType,
          stripe_product_id: product.id,
          stripe_price_id: priceId,
          monthly_rate: (price.unit_amount || 0) / 100,
          product_name: product.name,
        };

        // Upsert the mapping
        const { error } = await supabase
          .from('stripe_product_mappings')
          .upsert(mappingData, { onConflict: 'checkout_type' });

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "Product mappings saved successfully",
      });

      fetchData();
    } catch (error) {
      console.error('Error saving mappings:', error);
      toast({
        title: "Error",
        description: "Failed to save product mappings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Stripe Product Mappings</h1>
          <p className="text-muted-foreground">
            Configure which Stripe products are used for different checkout types
          </p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Box Storage Checkout</CardTitle>
              <CardDescription>
                Select the Stripe product/price used when a user subscribes to store a single box
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="box_storage">Product & Price</Label>
                <Select
                  value={selectedPrices.box_storage}
                  onValueChange={(value) =>
                    setSelectedPrices((prev) => ({ ...prev, box_storage: value }))
                  }
                >
                  <SelectTrigger id="box_storage">
                    <SelectValue placeholder="Select a product price" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) =>
                      product.prices.map((price) => (
                        <SelectItem key={price.id} value={price.id}>
                          {product.name} - ${(price.unit_amount || 0) / 100}/{price.currency}
                          {price.recurring && ` (${price.recurring.interval}ly)`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {mappings.box_storage && (
                  <p className="text-sm text-muted-foreground">
                    Current: {mappings.box_storage.product_name} - $
                    {mappings.box_storage.monthly_rate}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pickup Storage Checkout</CardTitle>
              <CardDescription>
                Select the Stripe product/price used when a user schedules a pickup for storage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="pickup_storage">Product & Price</Label>
                <Select
                  value={selectedPrices.pickup_storage}
                  onValueChange={(value) =>
                    setSelectedPrices((prev) => ({ ...prev, pickup_storage: value }))
                  }
                >
                  <SelectTrigger id="pickup_storage">
                    <SelectValue placeholder="Select a product price" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) =>
                      product.prices.map((price) => (
                        <SelectItem key={price.id} value={price.id}>
                          {product.name} - ${(price.unit_amount || 0) / 100}/{price.currency}
                          {price.recurring && ` (${price.recurring.interval}ly)`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {mappings.pickup_storage && (
                  <p className="text-sm text-muted-foreground">
                    Current: {mappings.pickup_storage.product_name} - $
                    {mappings.pickup_storage.monthly_rate}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 flex gap-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Mappings
          </Button>
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminStripeProducts;